import React, { useState, useEffect, useCallback } from 'react';
import { Wifi, WifiOff, Users, Link2, Unlink, RefreshCw, Server } from 'lucide-react';
import api from '../../services/api';
import MeshEnrollment from './MeshEnrollment';

const MeshSettings = () => {
  const [status, setStatus] = useState(null);
  const [peers, setPeers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEnrollment, setShowEnrollment] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      setLoading(true);
      const [statusResp, peersResp] = await Promise.all([
        api.get('/api/mesh/status'),
        api.get('/api/mesh/peers'),
      ]);
      setStatus(statusResp.data);
      setPeers(peersResp.data || []);
    } catch (err) {
      console.error('Mesh status fetch failed:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleUnlink = async () => {
    if (!window.confirm('Unlink your mesh identity? You will need to re-link to use mesh SSO.')) {
      return;
    }
    try {
      await api.post('/api/mesh/link-identity', { fingerprint: '' });
      fetchStatus();
    } catch (err) {
      alert('Failed to unlink: ' + (err.response?.data?.detail || err.message));
    }
  };

  if (loading && !status) {
    return (
      <div className="bg-virgo-navy-800 rounded-xl p-6 border border-virgo-teal-900">
        <div className="animate-pulse flex items-center gap-3">
          <div className="w-10 h-10 bg-virgo-navy-700 rounded-lg" />
          <div className="flex-1">
            <div className="h-4 bg-virgo-navy-700 rounded w-1/3 mb-2" />
            <div className="h-3 bg-virgo-navy-700 rounded w-1/2" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-virgo-navy-800 rounded-xl p-6 border border-virgo-teal-900">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            {status?.connected ? (
              <div className="p-2 bg-green-900/30 rounded-lg">
                <Wifi size={20} className="text-green-400" />
              </div>
            ) : (
              <div className="p-2 bg-red-900/30 rounded-lg">
                <WifiOff size={20} className="text-red-400" />
              </div>
            )}
            <div>
              <h3 className="text-white font-semibold">AstraeaMesh Network</h3>
              <p className="text-sm text-virgo-teal-400">
                {status?.connected ? 'Connected' : 'Disconnected'}
                {status?.peers_online > 0 && ` \u2022 ${status.peers_online} peers online`}
              </p>
            </div>
          </div>
          <button
            onClick={fetchStatus}
            className="p-2 text-virgo-teal-400 hover:text-white hover:bg-virgo-navy-700 rounded-lg transition-colors"
            title="Refresh"
          >
            <RefreshCw size={16} />
          </button>
        </div>

        {/* Identity Info */}
        <div className="space-y-3">
          {status?.user_linked ? (
            <div className="bg-virgo-navy-700/50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-virgo-teal-400">Linked Identity</span>
                <button
                  onClick={handleUnlink}
                  className="flex items-center gap-1 text-xs text-red-400 hover:text-red-300 transition-colors"
                >
                  <Unlink size={12} />
                  Unlink
                </button>
              </div>
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-virgo-teal-500">Fingerprint:</span>
                  <code className="text-xs text-virgo-teal-200 font-mono">
                    {status.fingerprint?.slice(0, 16)}...
                  </code>
                </div>
                {status.mesh_ip && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-virgo-teal-500">Mesh IP:</span>
                    <code className="text-xs text-virgo-teal-200 font-mono">{status.mesh_ip}</code>
                  </div>
                )}
                {status.hostname && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-virgo-teal-500">Hostname:</span>
                    <code className="text-xs text-virgo-teal-200 font-mono">{status.hostname}</code>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-virgo-navy-700/50 rounded-lg p-4">
              <p className="text-sm text-virgo-teal-300 mb-3">
                No mesh identity linked. Link your identity to enable mesh-based SSO and automatic login from the Virgo Browser.
              </p>
              <button
                onClick={() => setShowEnrollment(true)}
                className="flex items-center gap-2 px-4 py-2 bg-virgo-teal-600 hover:bg-virgo-teal-500 text-white rounded-lg transition-colors text-sm"
              >
                <Link2 size={16} />
                Link Identity
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Peer List */}
      {peers.length > 0 && (
        <div className="bg-virgo-navy-800 rounded-xl p-6 border border-virgo-teal-900">
          <div className="flex items-center gap-2 mb-4">
            <Users size={18} className="text-virgo-teal-400" />
            <h3 className="text-white font-semibold text-sm">Mesh Peers</h3>
            <span className="text-xs text-virgo-teal-500">({peers.length})</span>
          </div>
          <div className="space-y-2">
            {peers.map((peer) => (
              <div
                key={peer.fingerprint}
                className="flex items-center justify-between px-3 py-2 bg-virgo-navy-700/50 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <Server size={14} className="text-virgo-teal-500" />
                  <div>
                    <span className="text-sm text-white">{peer.hostname || 'Unknown'}</span>
                    <span className="text-xs text-virgo-teal-500 ml-2">{peer.mesh_ip}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {peer.tags?.map((tag) => (
                    <span
                      key={tag}
                      className="text-xs px-2 py-0.5 bg-virgo-navy-600 text-virgo-teal-300 rounded"
                    >
                      {tag}
                    </span>
                  ))}
                  <span
                    className={`w-2 h-2 rounded-full ${
                      peer.status === 'active' || peer.status === 'online'
                        ? 'bg-green-400'
                        : 'bg-gray-500'
                    }`}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Enrollment Modal */}
      {showEnrollment && (
        <MeshEnrollment
          onClose={() => setShowEnrollment(false)}
          onLinked={() => {
            setShowEnrollment(false);
            fetchStatus();
          }}
        />
      )}
    </div>
  );
};

export default MeshSettings;
